import { UserVehicle } from '../models'
import { fetchUserVehicles } from '../api'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { isUndefined } from 'lodash'
import { QueryKey } from '@tanstack/react-query'
import { TimeInMilliseconds } from 'src/Libs/types'
import { usePortalQuery } from 'src/Libs/api'

interface UseUserVehicles {
  vehicles: UserVehicle[] | null
  loading: boolean
  error?: Error | null
  hasError: boolean
  refetchVehicles: () => Promise<void>
}

const MAX_RETRY_ATTEMPTS = 5
const MAX_RETRY_INTERVAL = TimeInMilliseconds.thirtySecond

export const getUserVehiclesQueryKey = (installationId: number | undefined): QueryKey => ['vehicle-integration', 'userVehicles', installationId]

/**
 * Hook for handling user vehicles fetching
 */
const useUserVehicles = (installationId: number | undefined): UseUserVehicles => {
  const authFetch = useAuthFetchWithResponse()
  const isQueryEnabled = !isUndefined(installationId)

  const queryFn = async (): Promise<UserVehicle[] | null> => {
    const { error: fetchError, hasErrors, value } = await fetchUserVehicles(authFetch, installationId)

    if (hasErrors && fetchError) {
      throw new Error(fetchError?.message)
    }
    return value
  }

  const { data, isLoading, error, isError, refetch } = usePortalQuery({
    queryKey: getUserVehiclesQueryKey(installationId),
    queryFn,
    enabled: isQueryEnabled,
    retry: MAX_RETRY_ATTEMPTS,
    /** retries after 0.5, 1, 2, 4 and 8 seconds */
    retryDelay: attempt => {
      return Math.min(500 * 2 ** attempt, MAX_RETRY_INTERVAL)
    },
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: true,
  })

  const refetchVehicles = async (): Promise<void> => {
    if (isQueryEnabled) {
      await refetch()
    }
  }

  return {
    vehicles: data ?? null,
    loading: isLoading,
    error,
    hasError: isError,
    refetchVehicles,
  }
}

export default useUserVehicles
